import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { apiService, FeatureFlag, Tenant } from '../../services/api';

const FeatureFlagManagement: React.FC = () => {
    const [features, setFeatures] = useState<FeatureFlag[]>([]);
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Filters
    const [tenantFilter, setTenantFilter] = useState<string>('all');

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [editingFeature, setEditingFeature] = useState<FeatureFlag | null>(null);
    const [formData, setFormData] = useState({
        tenant_id: '',
        feature_name: '',
        allowed_roles: '*',
        is_active: true
    });
    const [submitting, setSubmitting] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [featuresData, tenantsData] = await Promise.all([
                apiService.getFeatureFlags(),
                apiService.getTenants()
            ]);
            setFeatures(featuresData);
            setTenants(tenantsData);
            setError(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenDialog = (feature?: FeatureFlag) => {
        if (feature) {
            setEditingFeature(feature);
            setFormData({
                tenant_id: feature.tenant_id,
                feature_name: feature.feature_name,
                allowed_roles: feature.allowed_roles || '*',
                is_active: feature.is_active
            });
        } else {
            setEditingFeature(null);
            setFormData({
                tenant_id: tenants.length > 0 ? tenants[0].tenant_id : '',
                feature_name: '',
                allowed_roles: '*',
                is_active: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingFeature(null);
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            if (editingFeature) {
                await apiService.updateFeatureFlag(editingFeature.id, {
                    allowed_roles: formData.allowed_roles,
                    is_active: formData.is_active
                });
            } else {
                await apiService.createFeatureFlag(formData);
            }
            handleCloseDialog();
            fetchData();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to save feature flag');
        } finally {
            setSubmitting(false);
        }
    };

    const handleToggleActive = async (feature: FeatureFlag) => {
        try {
            await apiService.updateFeatureFlag(feature.id, { is_active: !feature.is_active });
            fetchData();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to update flag status');
        }
    };

    const handleDelete = async (featureId: number) => {
        if (!window.confirm("Are you sure you want to delete this feature flag configuration?")) return;

        try {
            await apiService.deleteFeatureFlag(featureId);
            fetchData();
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Failed to delete flag');
        }
    };

    // Filter features based on tenant
    const filteredFeatures = features.filter((feature) => {
        return tenantFilter === 'all' || feature.tenant_id === tenantFilter;
    });

    if (loading && features.length === 0) return <CircularProgress />;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Feature Flag Access Controls</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Rule
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Tenant</InputLabel>
                    <Select
                        value={tenantFilter}
                        label="Filter by Tenant"
                        onChange={(e) => setTenantFilter(e.target.value as string)}
                    >
                        <MenuItem value="all">All Tenants</MenuItem>
                        {tenants.map(t => (
                            <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Tenant</strong></TableCell>
                            <TableCell><strong>Feature Key</strong></TableCell>
                            <TableCell><strong>Allowed Roles</strong></TableCell>
                            <TableCell><strong>Active</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredFeatures.map((feature) => (
                            <TableRow key={feature.id}>
                                <TableCell>{tenants.find(t => t.tenant_id === feature.tenant_id)?.name || 'Unknown'}</TableCell>
                                <TableCell><code>{feature.feature_name}</code></TableCell>
                                <TableCell>{feature.allowed_roles}</TableCell>
                                <TableCell>
                                    <Switch
                                        checked={feature.is_active}
                                        onChange={() => handleToggleActive(feature)}
                                        color="primary"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton onClick={() => handleOpenDialog(feature)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(feature.id)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                        {filteredFeatures.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={5} align="center">No feature flags configured.</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingFeature ? 'Edit Feature Flag Rule' : 'Add New Feature Flag Rule'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

                        <FormControl fullWidth disabled={!!editingFeature}>
                            <InputLabel>Tenant</InputLabel>
                            <Select
                                value={formData.tenant_id}
                                label="Tenant"
                                onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                                required
                            >
                                {tenants.map(t => (
                                    <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Feature Key"
                            fullWidth
                            required
                            helperText="e.g. 'show_logs', 'experimental_mapping'"
                            value={formData.feature_name}
                            onChange={(e) => setFormData({ ...formData, feature_name: e.target.value })}
                            disabled={!!editingFeature}
                        />

                        <TextField
                            label="Allowed Roles"
                            fullWidth
                            helperText="Comma separated roles, or '*' for all"
                            value={formData.allowed_roles}
                            onChange={(e) => setFormData({ ...formData, allowed_roles: e.target.value })}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>Enabled:</Typography>
                            <Switch
                                checked={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={
                            submitting ||
                            !formData.tenant_id ||
                            !formData.feature_name
                        }
                    >
                        {submitting ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FeatureFlagManagement;
